import { PhoneOutlined, PlusOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { Button, Divider, Form, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useSelectedBranch } from '../../../context/branchContext'
import useDebounce from '../../../shared/hooks/useDebounce'
import { NotFoundContent } from '../../../shared/utils/webHelpers'
import ImagePreview from '../ImagePreview'
import { BRANCH_DETAILS } from '@dentolize/clinic-mobile/src/shared/store/queries/companyQueries'
import { useUser } from '../../../context/userContext'
import { calculateMoney, formatAmount } from '../../../shared/utils/currencies'
import { useTranslation } from 'react-i18next'
import AmountBoxTooltip from '../../dashboard/inventories/AmountBoxTooltip'
import { trimSearchValue } from '@dentolize/clinic-mobile/src/shared/utils/helpers'
import InsurancePolicyForm from '../../dashboard/associates/InsurancePolicies/InsurancePolicyForm'
import ReferralSourceForm from '../../dashboard/settings/CompanyTypes/ReferralSources/ReferralSourceForm'
import ReferralSubSourceForm from '../../dashboard/settings/CompanyTypes/ReferralSubSources/ReferralSubSourceForm'
import PatientTagForm from '../../dashboard/settings/CompanyTypes/PatientTags/PatientTagForm'
import RejectReasonForm from '../../dashboard/settings/Leads/RejectReasons/RejectReasonForm'
import LeadStageForm from '../../dashboard/settings/Leads/LeadStages/LeadStageForm'
import InsuranceCompanyForm from '../../dashboard/associates/InsuranceCompanies/InsuranceCompanyForm'
import SuppliersForm from '../../dashboard/associates/Suppliers/SuppliersForm'
import LabsForm from '../../dashboard/associates/Labs/LabsForm'
import PolicyClassForm from '../../dashboard/associates/PolicyClasses/PolicyClassForm'
import moment from 'moment'
import PatientAppointmentsDetails from '../../dashboard/patients/Patient/components/PatientProfile/PatientAppointmentsDetails'
import { operationsStatusColors } from '../../../shared/utils/toothHelpers'

const newItemsObj = {
  searchInsurancePolicies: { Form: InsurancePolicyForm, permission: ['addNewInsurancePolicy'] },
  searchInsuranceCompanies: { Form: InsuranceCompanyForm, permission: ['addNewInsuranceCompany'] },
  searchPolicyClasses: { Form: PolicyClassForm, permission: ['addNewPolicyClass'] },
  searchRejectReasons: { Form: RejectReasonForm, permission: 'editLeadSettings' },
  searchPatientTags: { Form: PatientTagForm, permission: 'editCompanyTypes' },
  searchReferralSources: { Form: ReferralSourceForm, permission: 'editCompanyTypes' },
  searchReferralSubSources: { Form: ReferralSubSourceForm, permission: 'editCompanyTypes' },
  searchLeadStages: { Form: LeadStageForm, permission: 'editCompanyTypes' },
  searchSuppliers: { Form: SuppliersForm, permission: 'addNewLab' },
  searchLabs: { Form: LabsForm, permission: 'addNewLab' }
}

const dummyItem = {
  id: '1',
  new: true,
  fromDrawer: true
}

function renderCorrectOption({ item, type, passToQuery, locations, box, t, user }) {
  switch (type) {
    case 'searchGroups':
      return (
        <Space direction="vertical">
          <Tag color={item.color}>{item.name}</Tag>
          <Typography.Text type="secondary">{item.description || ''}</Typography.Text>
        </Space>
      )
    case 'searchMedications':
      return `${item.name}${item.type ? ` - ${item.type}` : ''}${item.strength ? ` - ${item.strength}` : ''}`
    case 'searchPatients':
      return (
        <Space direction="vertical" size={2}>
          <Space size={3} wrap>
            <Typography.Text strong>{item.name || `${item.firstName} ${item.lastName}`}</Typography.Text>
            {item.referenceId ? (
              <Tag type="secondary">#{item.referenceId}</Tag>
            ) : item.type === 2 ? (
              <Tag color="orange-inverse">{t('app.lead')}</Tag>
            ) : null}
            {item.patientId && <Typography.Text type="secondary">{item.patientId}</Typography.Text>}
          </Space>
          <Space size={3} wrap style={{ marginTop: 4 }}>
            <Typography.Text type="secondary">
              <PhoneOutlined /> {item.phoneNumber?.replace('_', ' ') || item.number?.replace('_', ' ') || ''}
            </Typography.Text>
            {item.prevAppointment || item.lastAppointment || item.activeAppointment ? (
              <PatientAppointmentsDetails patientDetails={item} />
            ) : null}
          </Space>
          {/* Insurance Details */}
          {item.insuranceCompany ? (
            <Space size={0} wrap style={{ marginTop: 4 }}>
              <Tooltip title={t('calendar.insuranceCompany')}>
                <Tag color="cyan">
                  <SafetyCertificateOutlined style={{ marginRight: 2 }} />
                  {item.insuranceCompany.name}
                </Tag>
              </Tooltip>
              {item.insurancePolicy ? (
                <Tooltip title={t('settings.insurance.insurancePolicy')}>
                  <Tag color="blue">{item.insurancePolicy.policyNumber}</Tag>
                </Tooltip>
              ) : null}
              {item.policyClass ? (
                <Tooltip title={t('settings.insurance.policyClass')}>
                  <Tag color="blue">{item.policyClass.name}</Tag>
                </Tooltip>
              ) : null}
              {!(item.policyClass ? item.policyClass.insuranceUnlimited : item.insuranceUnlimited) ? (
                <Tooltip title={t('calendar.insuranceLimit')}>
                  <Tag color="gold">
                    {calculateMoney((item.policyClass ? item.policyClass.insuranceLimit : item.insuranceLimit) || 0, user)}
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip title={t('calendar.unlimited')}>
                  <Tag color="purple">{t('calendar.unlimited')}</Tag>
                </Tooltip>
              )}
              <Tooltip title={t('settings.account.insurancePercentage')}>
                <Tag color="geekblue">
                  {(item.policyClass ? item.policyClass.insurancePercentage : item.insurancePercentage) || 0}%
                </Tag>
              </Tooltip>
              {item.insuranceEndDate ? (
                <Tooltip title={`${t('app.expires')} ${moment(item.insuranceEndDate).format('ll')}`}>
                  <Tag color={moment(item.insuranceEndDate).isBefore(moment()) ? 'red-inverse' : 'green-inverse'}>
                    {moment(item.insuranceEndDate).fromNow()}
                  </Tag>
                </Tooltip>
              ) : null}
            </Space>
          ) : null}
        </Space>
      )
    case 'searchProcedures':
    case 'searchProcedureSteps':
      return (
        <Typography.Text>
          <Typography.Text type="secondary">
            {`${
              item.procedure?.group
                ? `${item.procedure.group.name} - ${item.procedure.code} - ${item.procedure.name} - `
                : passToQuery.procedureGroup
                ? `${item.code} - `
                : `${item.group?.name} - ${item.code} - `
            }`}
          </Typography.Text>
          {item.name}
        </Typography.Text>
      )
    case 'searchInventories':
    case 'searchInventoryItems':
    case 'searchInventorySubItems':
    case 'searchMasterInventories':
      return (
        <Space>
          {item.images?.length ? <ImagePreview size={30} src={item.images[0]} /> : null}
          <Typography.Text strong type={item.amount && item.amount < item.alertIfLessThan ? 'danger' : null}>
            {item.name}
          </Typography.Text>
          {item.amount && item.amountBox ? (
            <AmountBoxTooltip tooltip={false} amount={item.amount} amountBox={item.amountBox} minAmount={item.minAmount} />
          ) : item.amount ? (
            <Tag className="hide-on-select" color={item.amount < item.alertIfLessThan ? 'red' : 'green'}>
              {Number(item.amount.toFixed(2))}
            </Tag>
          ) : null}
          {item.branch ? (
            <Tag className="hide-on-select">
              {item.branch.name} - {item.room || ''}
            </Tag>
          ) : null}
        </Space>
      )
    case 'searchInventoryStorages':
      return (
        <Space>
          <Tag color={item.isMaster ? 'red-inverse' : 'blue-inverse'}>{item.isMaster ? 'M' : 'B'}</Tag>
          <Typography.Text strong>{item.name}</Typography.Text>
          {locations?.length &&
          locations.find(s => s.inventorySubItemId === passToQuery?.inventorySubItem && s.inventoryStorageId === item.id) ? (
            <Tag color="green" className="hide-on-select">
              {formatAmount(
                locations.find(s => s.inventorySubItemId === passToQuery?.inventorySubItem && s.inventoryStorageId === item.id)
                  .amount
              )}
              {box &&
              box !== 1 &&
              locations.find(s => s.inventorySubItemId === passToQuery?.inventorySubItem && s.inventoryStorageId === item.id)
                .amount
                ? ` ~ ${formatAmount(
                    locations.find(
                      s => s.inventorySubItemId === passToQuery?.inventorySubItem && s.inventoryStorageId === item.id
                    ).amount / box
                  )}`
                : ''}
            </Tag>
          ) : null}
        </Space>
      )
    case 'searchLabItems':
      return (
        <Space>
          {item.images?.length ? <ImagePreview size={30} src={item.images[0]} /> : null}
          <Typography.Text strong>{item.name}</Typography.Text>
          <Tag>{item.lab?.name}</Tag>
        </Space>
      )
    case 'searchInsurancePolicies':
      return (
        <Space size={4} wrap split="|">
          <Typography.Text strong>{item.policyNumber}</Typography.Text>
          <Typography.Text>{item.holderName}</Typography.Text>
          <Typography.Text type={item.due && new Date(item.due) < new Date() ? 'danger' : 'success'}>
            {item.due ? moment(item.due).format('LL') : ''}
          </Typography.Text>
        </Space>
      )
    case 'searchPatientOperations':
      return (
        <Space direction="vertical">
          <Space size={8} align="center">
            {item.code ? <Typography.Text type="secondary">{item.code} -</Typography.Text> : null}
            <Typography.Text>{item.name}</Typography.Text>
            <Tag color={operationsStatusColors[item.status]} className="min-width-status">
              {item.status.slice(0, item.status === 'IN_PROGRESS' ? 2 : 1)}
            </Tag>
          </Space>
          <Space size={4} split="-">
            <Typography.Text type="secondary">{moment(item.createdAt).format('LL')}</Typography.Text>
            <Typography.Text>{item.doctor?.name || ''}</Typography.Text>
          </Space>
        </Space>
      )
    default:
      return (
        <Space>
          {item.images?.length ? <ImagePreview size={30} src={item.images[0]} /> : null}
          {item.code ? <Typography.Text type="secondary">{item.code} -</Typography.Text> : null}
          {item.color && !item.disabled ? (
            <Tag color={item.color}>{item.name || item.companyName || item.inventoryStorage?.name}</Tag>
          ) : (
            <Typography.Text type={item.disabled ? 'danger' : null}>
              {item.name || item.companyName || item.inventoryStorage?.name}
            </Typography.Text>
          )}
          {item.view && item.order ? <Typography.Text>{`${item.view.name} (${item.order})`}</Typography.Text> : null}
          {item.referralSource && item.order ? (
            <Typography.Text>{`${item.referralSource.name} (${item.order})`}</Typography.Text>
          ) : null}
        </Space>
      )
  }
}

// Helper to convert item to Select item object
function toSelectItem(item, dataName, allowInactive, passToQuery, locations, box, t, user) {
  const title =
    item.name ||
    (item.holderName ? `${item.holderName} ${item.policyNumber || ''}` : null) ||
    item.policyNumber ||
    (item.firstName ? `${item.firstName} ${item.lastName || ''}` : null) ||
    item.fullName ||
    ''

  return {
    label: renderCorrectOption({ item, type: dataName, passToQuery, locations, box, t, user }),
    value: item.id,
    key: item.id,
    item,
    name: title,
    title,
    disabled:
      (item.active === false && !allowInactive) ||
      (!allowInactive &&
        (item.prices?.length && passToQuery?.priceList
          ? item.prices.find(s => s.parentId === passToQuery?.priceList)?.active === false
          : item.active === false))
  }
}

const SearchField = props => {
  const {
    label,
    placeholder = '',
    GET_QUERY,
    fieldName,
    dataName,
    onSelect,
    rules = [],
    multiple = false,
    allowClear = false,
    noStyle,
    style,
    validateStatus,
    help,
    value = undefined,
    patientSearchFieldRef,
    filterPriceLists,
    filterItems,
    lab,
    disabled,
    branchValue,
    isLoading,
    size,
    disableAddNew,
    autoFocus,
    room,
    location,
    noBranchId,
    popupMatchSelectWidth = false,
    skip,
    formName,
    permission,
    allowInactive,
    allowNew,
    box,
    passToQuery = {},
    dataTestId,
    setFieldsValue,
    getFieldValue
  } = props

  const [searchTerm, setSearchTerm] = useState('')
  const [touched, setTouched] = useState(false)
  const [open, setOpen] = useState(false)
  const [formVisible, setFormVisible] = useState(false)

  const { selectedBranch } = useSelectedBranch()
  const { user } = useUser()
  const { t } = useTranslation()

  const { data: branchData } = useQuery(BRANCH_DETAILS, {
    variables: { branch: selectedBranch },
    fetchPolicy: 'cache-first',
    skip: !selectedBranch || !filterPriceLists
  })

  const debouncedSearchTerm = useDebounce(searchTerm, 700)

  const { data, loading, networkStatus } = useQuery(GET_QUERY, {
    variables: {
      searchTerm: searchTerm === '' ? searchTerm : trimSearchValue(debouncedSearchTerm),
      lab,
      branchId:
        !noBranchId && ['searchCounters', 'searchInventories'].includes(dataName) ? branchValue || selectedBranch : undefined,
      room: ['searchCounters', 'searchInventories'].includes(dataName) ? room : undefined,
      location: dataName === 'searchMasterInventories' ? location : undefined,
      ...passToQuery
    },
    fetchPolicy: 'cache-first',
    skip:
      !touched ||
      skip ||
      (user && permission && !user.permissions[permission]) ||
      (!noBranchId && ['searchCounters', 'searchInventories'].includes(dataName) && !(branchValue || selectedBranch))
  })

  const items = useMemo(() => {
    if (loading || debouncedSearchTerm !== searchTerm || !(networkStatus === 7 || networkStatus === 8)) {
      return [
        {
          label: (
            <div className="center">
              <Spin />
            </div>
          ),
          value: 'loader',
          key: 'loader',
          disabled: true
        }
      ]
    }
    const itemsArr = data
      ? filterPriceLists && branchData
        ? data[dataName].filter(item => !!branchData.branchDetails.availablePriceLists.find(p => p.id === item.id))
        : fieldName === 'doctor' && dataName === 'searchUsers'
        ? data[dataName].filter(u => u.isDoctor)
        : dataName === 'searchInventoryStorages'
        ? data[dataName].storages
        : data[dataName]
      : []
    const selectItems =
      (filterItems ? itemsArr.filter(filterItems) : itemsArr)?.map(item =>
        toSelectItem(
          item,
          dataName,
          allowInactive,
          passToQuery,
          data && dataName === 'searchInventoryStorages' ? data[dataName].locations : null,
          box,
          t,
          user
        )
      ) ?? []
    // Add allowNew option if needed
    if (
      allowNew &&
      searchTerm &&
      data &&
      !data[dataName]?.length &&
      !(isLoading || loading || debouncedSearchTerm !== searchTerm || !(networkStatus === 7 || networkStatus === 8))
    ) {
      selectItems.push({
        label: (
          <Space className="flex-container justify-between">
            {searchTerm}
            <Tag icon={<PlusOutlined />}>{t('settings.groups.addNew')}</Tag>
          </Space>
        ),
        value: searchTerm,
        key: 'new-item',
        name: searchTerm,
        item: { name: searchTerm }
      })
    }
    return selectItems
  }, [loading, data, networkStatus, searchTerm, debouncedSearchTerm])

  const newItemObj = useMemo(() => newItemsObj[dataName], [dataName])

  const handleSearchField = value => {
    if (!value) {
      setSearchTerm(value)
      return
    }
    if (searchTerm !== value) {
      setSearchTerm(value)
    }
  }

  const handleSelect = (item, el) => {
    handleSearchField('')
    onSelect && onSelect(el)
  }

  const handleTouch = () => {
    if (touched) return
    setTouched(true)
  }

  const handleBlur = () => {
    if (!searchTerm) return
    setSearchTerm('')
  }

  const handleFormVisibility = newItem => {
    setFormVisible(v => !v)
    setOpen(false)

    if (setFieldsValue && newItem?.id) {
      const itemValue = {
        value: newItem.id,
        key: newItem.id,
        label: renderCorrectOption({ item: newItem, type: dataName, passToQuery, t, user }),
        item: newItem
      }

      if (multiple && getFieldValue) {
        const values = getFieldValue(fieldName) || []
        setFieldsValue({ [fieldName]: [...values, itemValue] })
      } else if (!multiple) {
        setFieldsValue({ [fieldName]: itemValue })
      }
    }
  }

  const NewItemForm = newItemObj?.Form

  return (
    <div>
      <Form.Item name={fieldName} rules={rules} label={label} noStyle={noStyle} validateStatus={validateStatus} help={help}>
        <Select
          onFocus={handleTouch}
          open={open}
          className="fixArrowSelection"
          onDropdownVisibleChange={setOpen}
          dropdownRender={originNode => (
            <>
              {originNode}
              {newItemObj && user ? (
                <>
                  <Divider dashed style={{ margin: '3px 0' }} />
                  <Button
                    onClick={handleFormVisibility}
                    disabled={
                      disableAddNew ||
                      (!newItemObj.permission
                        ? true
                        : typeof newItemObj.permission === 'string'
                        ? !user.permissions[newItemObj.permission]
                        : !newItemObj.permission.some(s => !!user.permissions[s]))
                    }
                    block
                    type="text"
                    icon={<PlusOutlined />}
                  >
                    {t('settings.groups.addNew')}
                  </Button>
                </>
              ) : null}
            </>
          )}
          optionFilterProp="name"
          labelInValue
          labelRender={item => item?.title || item?.label}
          mode={multiple ? 'multiple' : ''}
          showSearch
          allowClear={allowClear}
          placeholder={placeholder}
          showAction={['click', isMobile ? '' : 'focus']}
          onSearch={handleSearchField}
          loading={isLoading || loading || debouncedSearchTerm !== searchTerm || !(networkStatus === 7 || networkStatus === 8)}
          notFoundContent={NotFoundContent(loading, debouncedSearchTerm !== searchTerm, networkStatus)}
          onSelect={handleSelect}
          onDeselect={multiple ? handleSelect : undefined}
          style={style}
          onClear={handleSelect}
          onBlur={handleBlur}
          value={value}
          ref={patientSearchFieldRef}
          disabled={disabled}
          size={size}
          autoFocus={autoFocus}
          popupMatchSelectWidth={popupMatchSelectWidth}
          data-testid={dataTestId || `${formName || 'searchField'}-${fieldName || dataName}`}
          options={items}
          filterOption={() => true}
        />
      </Form.Item>
      {NewItemForm ? (
        <NewItemForm
          formVisible={formVisible}
          handleClose={handleFormVisibility}
          selectedItem={{ ...dummyItem, ...passToQuery }}
        />
      ) : null}
    </div>
  )
}

SearchField.propTypes = {
  GET_QUERY: PropTypes.object.isRequired,
  label: PropTypes.any,
  fieldName: PropTypes.any,
  dataName: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  rules: PropTypes.array,
  multiple: PropTypes.bool,
  onSelect: PropTypes.func,
  setFieldsValue: PropTypes.func,
  getFieldValue: PropTypes.func,
  allowClear: PropTypes.bool,
  filterPriceLists: PropTypes.bool,
  // which of the items found may be chosen here
  filterItems: PropTypes.func,
  noStyle: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  noBranchId: PropTypes.bool,
  validateStatus: PropTypes.string,
  lab: PropTypes.string,
  branchValue: PropTypes.string,
  size: PropTypes.string,
  autoFocus: PropTypes.bool,
  disableAddNew: PropTypes.bool,
  allowNew: PropTypes.bool,
  help: PropTypes.any,
  room: PropTypes.string,
  location: PropTypes.string,
  permission: PropTypes.string,
  style: PropTypes.object,
  value: PropTypes.object,
  skip: PropTypes.bool,
  allowInactive: PropTypes.bool,
  passToQuery: PropTypes.object,
  patientSearchFieldRef: PropTypes.object
}

export default SearchField
